import { QueryKey } from '@/enums';
import { newQueryClient } from '@/queries/reactQueryConfig';
import { getToken } from '@/utils';
import { get } from 'lodash';

// SSO tenants sign in through a LILAC session (BFF pattern, see amili-api
// docs/auth-migration-plan.md §10): the API authenticates this browser by an
// HttpOnly cookie, so there is no token here at all. This marker takes the
// tokens' place in localStorage['auth'] so every existing "is signed in"
// check (`!!getToken('accessToken')`, PrivateRoute) keeps working; it is
// never sent as a bearer token.
export const SSO_SESSION_TOKEN = 'sso-session';

export const SSO_SESSION_AUTH = {
  accessToken: SSO_SESSION_TOKEN,
  idToken: SSO_SESSION_TOKEN,
  refreshToken: '',
};

// Required by the API on state-changing cookie-authenticated requests (CSRF).
export const CSRF_HEADER = 'X-Lilac-Request';

export const isSsoSession = () => getToken('accessToken') === SSO_SESSION_TOKEN;

/** Sends cookie-authenticated requests the way the API expects them. */
export const withSsoSession = (request: any) => {
  delete request.headers.Authorization;
  request.withCredentials = true;
  request.headers[CSRF_HEADER] = '1';

  return request;
};

const getCurrentSubdomain = () =>
  get(newQueryClient.getQueryData([QueryKey.SUB_DOMAIN]), 'data.subdomain') as
    | string
    | undefined;

/**
 * Hands the browser to Amili SSO, which returns it to this tenant's own
 * /auth/callback (auth-service accepts any https *.amili.asia for sign-in).
 * With the SSO session still alive this is a silent ~1 s round trip;
 * otherwise the user signs in. Invite links instead use the bare host's
 * callback with ?tenant= — the invite allowlist has no wildcards.
 *
 * `force` skips that silent round trip and always shows the sign-in form, so
 * signing out can't land the user straight back in — and whoever signs in
 * next replaces the SSO session.
 */
export const redirectToSsoLogin = (
  subdomain = getCurrentSubdomain(),
  { force = false } = {},
): boolean => {
  const ssoLoginUrl = process.env.REACT_APP_SSO_LOGIN_URL;

  if (!ssoLoginUrl || !subdomain) {
    return false;
  }

  const callbackUrl = `${window.location.origin}/auth/callback`;

  window.location.assign(
    `${ssoLoginUrl}?redirect_uri=${encodeURIComponent(callbackUrl)}${
      force ? '&force=1' : ''
    }`,
  );

  return true;
};
